import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Provider } from 'react-redux';
import { compose } from 'redux';
import { useFormik } from 'formik';
import { isNil } from 'ramda';
import { toast } from 'react-toastify';

import store from '../../../store';
import { bonusRestHooks } from '../../../store/Bonus/BonusSlice';
import { messagesRestHooks } from '../../../store/Messages/MessagesSlice';
import FormHeader from '../../../components/FormHeader';
import Label from '../../../components/Label';
import Group from '../../../components/Group';
import Column from '../../../components/Column';
import { ErrorFlash } from '../../../components';
import { useFetchStatus } from '../../../utils/fetchStatusUtils';
import StoreSelect from '../../../components/StoreSelect';
import {
  validationSchema,
  initialValues,
} from '../../../forms/Bonus/newBonusForm';
import MessageSelect from '../../../containers/SendMessageContainer/components/SendMessageForm/components/MessageSelect';
import { storesRestHooks } from '../../../store/Stores/StoresSlice';
import { STORE_DEFAULT } from '../../constants';

function BonusNew(props) {
  const history = useHistory();

  const { createBonus, bonus } = bonusRestHooks.use();

  const {
    loadMessages,
    loadResetMessages,
    messages: { loadingStatus, items: messages },
  } = messagesRestHooks.use();

  const {
    loadStores,
    stores: { loadingStatus: storesLoadingState, items: stores },
  } = storesRestHooks.use();

  const { isFulfilled } = useFetchStatus(loadingStatus);

  const notify = () =>
    toast.success('Bonus Successfully Created!', {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });

  const newBonus = async (values) => {
    const { messageId, amount, description, storeIdEq, comment } = values;
    const body = {
      messageId: messageId,
      amount: Number(amount),
      description: description,
      storeId: storeIdEq,
      auditComment: comment,
    };

    const userId = props.match.params.userId;
    await createBonus(body, userId);
    if (isNil(bonus.errors)) {
      history.goBack();
      notify();
    }
  };

  const formik = useFormik({
    initialValues: initialValues(messages, stores),
    onSubmit: newBonus,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
  });

  const { values, handleSubmit, setFieldValue, errors, setErrors } = formik;

  const handleChange = ({ target }) => {
    const { id, value } = target;
    setFieldValue(id, value);
  };

  useEffect(() => {
    loadMessages();
    loadStores();
    return () => {
      loadResetMessages();
    };
  }, []);

  return (
    <div className="store-new">
      <ErrorFlash errors={bonus.errors} message={bonus.errors} />
      {isFulfilled && (
        <form className="store-form" onSubmit={handleSubmit}>
          <FormHeader title="New Bonus">
            <input
              id="saveStore"
              className="store-submit"
              type="submit"
              defaultValue="Save"
            />
          </FormHeader>
          <div className="form-fields">
            <Group>
              <div className="form-group-fields">
                <Column>
                  <div className="input">
                    <Label name="amount" title="Amount" errors={errors} />
                    <input
                      id="amount"
                      value={values.amount}
                      onChange={handleChange}
                      required={true}
                    />
                  </div>
                  <div className="input">
                    <Label
                      name="description"
                      title="Description"
                      errors={errors}
                    />
                    <input
                      id="description"
                      value={values.description}
                      onChange={handleChange}
                      required={true}
                    />
                  </div>
                  <div className="input">
                    <Label
                      name="comment"
                      title="Comment (for audit purposes)"
                      errors={errors}
                    />
                    <input
                      id="comment"
                      value={values.comment}
                      onChange={handleChange}
                      required={true}
                    />
                  </div>
                  <div className="input">
                    <Label name="storeIdEq" title="Store" errors={errors} />
                    <StoreSelect
                      stores={stores}
                      onChange={handleChange}
                      value={values.storeIdEq}
                      loadingState={storesLoadingState}
                      defaultOption={
                        stores
                          ?.filter((item) =>
                            item?.name?.includes(STORE_DEFAULT)
                          )
                          .map((item) => ({
                            value: item.id,
                            label: item.name,
                          }))[0]
                      }
                    />
                  </div>
                  <div className="input">
                    <MessageSelect
                      value={values.messageId}
                      onChange={handleChange}
                      messages={messages}
                      isFulfilled={isFulfilled}
                      errors={errors}
                    />
                  </div>
                </Column>
              </div>
            </Group>
          </div>
        </form>
      )}
    </div>
  );
}
const addStore = (ProxyComponent) => (props) =>
  (
    <Provider store={store}>
      <ProxyComponent {...props} />
    </Provider>
  );

export default compose(addStore)(BonusNew);
